import { ChangeDetectionStrategy, Component, EventEmitter, Inject, isDevMode, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { IAppRequest } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { NotifyMenuActions, NotifySortField, NotifyStatuses } from '@app/layout/side-menu/constains/Notify.constains';
import {
    IGetNotifyListParams,
    IGetNotifyListParamsForm,
    INotify,
    INotifyListComponentRegisters,
    TNotifyMenuActions,
    TNotifyStatus,
} from '@app/layout/side-menu/interfaces/Notify.interface';
import { NotificationsService } from '@app/modules/notifications/notifications.service';
import { IButtonExtended } from '@components/button/interfaces/button.interface';
import { IOutputEventData } from '@components/buttons-panel/buttons-panel.interface';
import { LANG_DICTIONARY_TOKEN, SKELETON_LOADER_TOKEN } from '@constants/diConstant';
import { IMainLang } from '@interfaces/languages/main-lang';
import { InterfaceAccessMatrix } from '@modules/login/interfaces/login.interface';
import { TuiDestroyService } from '@taiga-ui/cdk';
import { NgxSkeletonLoaderConfigTheme } from 'ngx-skeleton-loader';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'notify-list',
    templateUrl: './notify-list.component.html',
    styleUrls: ['../styles/notify-list.component.scss', '../styles/notify-list.component.adaptive.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [TuiDestroyService],
})
export class NotifyListComponent implements OnInit {
    public skeletonArrCount = new Array(1);
    /**
     * Список кнопок
     * @private
     */
    private readonly _buttons: BehaviorSubject<IButtonExtended[]> = new BehaviorSubject<IButtonExtended[]>([]);
    /**
     * Список уведомлений
     * @private
     */
    private readonly _list$: BehaviorSubject<INotify[]> = new BehaviorSubject<INotify[]>([]);
    private readonly _isLoad$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public readonly NOTIFY_STATUSES = NotifyStatuses;

    public form: FormGroup<IGetNotifyListParamsForm>;

    public get notifyList$(): Observable<INotify[]> {
        return this._list$.asObservable().pipe(takeUntil(this._destroy$));
    }

    public get isLoad$(): Observable<boolean> {
        return this._isLoad$;
    }

    public get buttons$(): Observable<IButtonExtended[]> {
        return this._buttons;
    }

    public readonly registers: INotifyListComponentRegisters = {
        statuses: [],
        sortFiled: [],
    };

    /** Событие для вызова закрытия списка уведомлений */
    @Output() close: EventEmitter<void> = new EventEmitter();

    constructor(
        @Inject(LANG_DICTIONARY_TOKEN) public readonly dictionary: IMainLang = null,
        @Inject(SKELETON_LOADER_TOKEN) public readonly skeletonConf: NgxSkeletonLoaderConfigTheme = null,
        public readonly appService: AppService,
        private readonly _spinner: NgxSpinnerService,
        @Inject(TuiDestroyService) private _destroy$: TuiDestroyService,
        private readonly _fb: FormBuilder,
        private readonly _router: Router,
        private readonly notifyService: NotificationsService,
    ) {
        this.form = this.initForm();
        this.fillButtons(this.appService.accessMatrix);
        this.fillRegisters();
        if (notifyService.unreadCount !== 0) {
            this.skeletonArrCount = new Array(notifyService.unreadCount < 10 ? notifyService.unreadCount : 10);
        }
    }

    /**
     * Получить список уведомлений
     * @param params - параметры запроса
     */
    private getList(params: IGetNotifyListParams): void {
        this.notifyService
            .getShortList(params)
            .pipe(
                map((result: IAppRequest) => {
                    if (!result || result.is_error === true || !result.data || !Array.isArray(result.data)) {
                        throw new Error(result?.message);
                    }
                    return result.data;
                }),
                takeUntil(this._destroy$),
            )
            .subscribe({
                next: (list) => {
                    if (params.status === NotifyStatuses.new) {
                        this.notifyService.unreadCount = list.length;
                    }
                    this._list$.next(list);
                    this._isLoad$.next(true);
                },
                error: (err: unknown) => {
                    if (isDevMode()) {
                        console.error(err);
                    }
                    this.close.emit();
                },
            });
    }

    /**
     * Инициализация формы
     * @private
     */
    private initForm(): FormGroup {
        const form = this._fb.group({
            status: [NotifyStatuses.new, []],
            order: [NotifySortField.datetimeDesc, []],
        });

        this.isLoad$.pipe(takeUntil(this._destroy$)).subscribe((flag) => {
            if (flag) {
                ['status', 'order'].forEach((key) => {
                    form.get(key).enable({ emitEvent: false, onlySelf: true });
                });
            } else {
                ['status', 'order'].forEach((key) => {
                    form.get(key).disable({ emitEvent: false, onlySelf: true });
                });
            }
        });

        form.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((data) => {
            this._isLoad$.next(false);
            this.getList(data as IGetNotifyListParams);
        });
        return form as FormGroup;
    }

    /**
     * Заполнение справочников
     * @private
     */
    private fillRegisters(): void {
        this.registers.statuses.push(
            {
                id: 'all',
                name: this.dictionary?.log_event_status_all,
            },
            {
                id: NotifyStatuses.read,
                name: this.dictionary?.msg_read,
            },
            {
                id: NotifyStatuses.new,
                name: this.dictionary?.system_new,
            },
        );
        this.registers.sortFiled.push({
            id: NotifySortField.datetimeDesc,
            name: this.dictionary?.notifications_date_sort_desc,
        });
        this.registers.sortFiled.push({
            id: NotifySortField.datetimeAsc,
            name: this.dictionary?.notifications_date_sort_asc,
        });
    }

    /**
     * Кнопки действий для меню
     * @param accessMatrix - матрица доступов
     * @private
     */
    private fillButtons(accessMatrix: InterfaceAccessMatrix): void {
        const arr: IButtonExtended[] = [];
        arr.push({
            label: this.dictionary?.notify_mark_all_read,
            title: this.dictionary?.notify_mark_all_read,
            click: NotifyMenuActions.markAllRead,
            icon: 'checkbox-multiple-marked',
        });
        if (accessMatrix?.notification_all?.show) {
            arr.push({
                label: this.dictionary?.open_notify_table,
                title: this.dictionary?.open_notify_table,
                click: NotifyMenuActions.openNotifyTable,
                icon: 'bell',
            });
        }

        this._buttons.next(arr);
    }

    /**
     * Обработка событий компонента кнопки-меню
     * @param $event
     */
    public clickMenuButton($event: IOutputEventData<TNotifyMenuActions>): void {
        switch ($event.name) {
            case NotifyMenuActions.openNotifyTable:
                this._router.navigateByUrl(this._router.createUrlTree(['notification_all'])).then();
                break;
            case NotifyMenuActions.markAllRead:
                this.notifyService
                    .markAsReadAll()
                    .pipe(takeUntil(this._destroy$))
                    .subscribe(() => {
                        this.notifyService.unreadCount = 0;
                        this._isLoad$.next(false);
                        this.getList(this.form.getRawValue());
                    });
                break;
        }
    }

    public ngOnInit(): void {
        this.getList(this.form.getRawValue());
    }

    /**
     * Изменение статуса
     * @param item - данные уведомления
     * @param status - статус, который надо проставить
     */
    public changeStatus(item: INotify, status: TNotifyStatus): void {
        if (item.status === status) {
            return;
        }
        item.status = status;
        this.notifyService.markAsRead({ id: item.id }).pipe(take(1), takeUntil(this._destroy$)).subscribe();
    }
}
